import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isLsCoreNursingProcedureContentCode, isAoDiseaseCareContentCode } from '@/constants/scenarioTypes';
import { DesignDataCell } from '@/components/result/DesignDataCell';
import { CommentToggleButton, ExpandableCommentTableRows } from './CommentExpand';
import { ResultSectionWithTitle } from './ResultSectionEmpty';
import { RESULT_DESIGN_PLACEHOLDER } from '@/constants/resultDesignPlaceholder';
import { Activity, ChevronDown } from 'lucide-react';
import { ScenarioResultBadge } from './ScenarioResultBadge';
import { ProcedureReferenceCellContent } from '@/utils/evaluationResultUtils';
import { isScenarioAnswerCorrect } from '@/utils/scenarioResultFormat';

type ProcedureResultType =
  | 'iv'
  | 'blood_draw'
  | 'acls'
  | 'resp_infect'
  | 'competency'
  | 'disease_care'
  | 'ppe'
  | 'fallback';

const PL = RESULT_DESIGN_PLACEHOLDER;

/** 데이터 없을 때 — IV 절차표와 동일한 열·가독을 위한 정적 스켈레톤(획득 점수 열 포함) */
function ProcedurePerformanceTableSkeleton({ showScoreColumn = true }: { showScoreColumn?: boolean }): React.ReactElement {
  const { t: tScenario } = useTranslation('scenario');
  const resultHeaderLabel = tScenario('ivResult.tableResult');
  return (
    <div
      className="rounded-xl border border-gray-200 border-s-4 border-s-sky-500/45 overflow-hidden bg-white overflow-x-auto"
      role="presentation"
    >
      <table className="w-full text-sm text-start">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200 text-center">
            <th className="px-4 py-3 font-semibold text-gray-700 w-12 normal-case text-center">
              {tScenario('ivResult.tableNo')}
            </th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-start">
              {tScenario('ivResult.tableEvaluationItem')}
            </th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-start">
              {tScenario('ivResult.tableCorrect')}
            </th>
            <th className="px-4 py-3 font-semibold text-gray-700 text-center">{resultHeaderLabel}</th>
            {showScoreColumn && (
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">
                {tScenario('ivResult.tableScore')}
              </th>
            )}
            <th className="px-4 py-3 font-semibold text-gray-700 w-24 whitespace-nowrap text-center">
              {tScenario('ivResult.tableComment')}
            </th>
          </tr>
        </thead>
        <tbody>
          {[0, 1, 2].map((r) => {
            const stripe = (r + 1) % 2 === 0 ? 'bg-slate-50/50' : 'bg-white';
            return (
            <tr className={`${stripe} border-b border-slate-100/80 last:border-0`} key={`proc-skel-${r}`}>
              <td className="px-4 py-3 text-center text-slate-600 font-medium normal-case align-top">{r + 1}</td>
              <td className="px-4 py-3 align-top">
                <DesignDataCell hint="평가·항목" align="left" />
              </td>
              <td className="px-4 py-3 align-top">
                <DesignDataCell hint="정답(참고·선택)" align="left" />
              </td>
              <td className="px-4 py-3 align-top">
                <div className="text-center text-sm text-slate-800 font-semibold">
                  {PL}
                </div>
                <div className="text-center text-[10px] text-slate-400 font-normal normal-case leading-tight mt-0.5">O/X</div>
              </td>
              {showScoreColumn && (
                <td className="px-4 py-3 text-center font-medium align-top text-slate-800">
                  <div>{PL}</div>
                  <div className="text-[10px] text-slate-400 font-normal normal-case leading-tight mt-0.5">획득</div>
                </td>
              )}
              <td className="px-4 py-3 text-center align-top text-slate-500">
                <ChevronDown className="mx-auto" size={18} aria-hidden />
                <div className="text-[10px] text-slate-400 font-normal normal-case leading-tight mt-1.5">코멘트</div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface ProcedureViewProps {
  title: string;
  rows: any[];
  patientInfo?: any;
  prescribedOrder?: string;
  resultType: ProcedureResultType;
  expandedIndex?: number | null;
  setExpandedIndex?: React.Dispatch<React.SetStateAction<number | null>>;
  contentCode?: string | null;
}

/** 단일 절차 유형(IV·채혈·ACLS 등) 수행결과 블록 */
function ProcedureView({
  title,
  rows,
  patientInfo,
  prescribedOrder,
  resultType,
  expandedIndex,
  setExpandedIndex,
  contentCode,
}: ProcedureViewProps): React.ReactElement | null {
  const { t } = useTranslation('class');
  const { t: tScenario } = useTranslation('scenario');
  const [internalExpandedIndex, setInternalExpandedIndex] = useState<number | null>(null);
  
  // LS 또는 AO 컨텐츠인지 확인
  const isLsContent = isLsCoreNursingProcedureContentCode(contentCode ?? null);
  const isAoContent = isAoDiseaseCareContentCode(contentCode ?? null);
  const hideScoreColumn = isLsContent || isAoContent;
  
  if (!rows || rows.length === 0) {
    return (
      <div className="space-y-4 !mt-6 !mb-4">
        <h3 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2.5 min-w-0">
          <div className="w-9 h-9 flex items-center justify-center rounded-lg border shrink-0 bg-sky-200/55 border-sky-200/80">
            <Activity size={20} className="text-sky-800" />
          </div>
          <span className="truncate min-w-0">{title}</span>
        </h3>
        <ProcedurePerformanceTableSkeleton showScoreColumn={!hideScoreColumn} />
      </div>
    );
  }

  const hasPatientInfo = patientInfo && (
    patientInfo.name != null
    || patientInfo.age != null
    || patientInfo.gender != null
    || patientInfo.registrationNo != null
  );
  // LS 또는 AO 컨텐츠는 점수 컬럼 숨김
  const showScoreColumn = !hideScoreColumn && rows.some((row) => row.score != null && row.score !== '');
  const currentExpandedIndex = expandedIndex ?? internalExpandedIndex;
  const updateExpandedIndex = setExpandedIndex ?? setInternalExpandedIndex;
  const resultHeaderLabel = resultType === 'resp_infect'
    ? tScenario('accuracy')
    : tScenario('ivResult.tableResult');

  const getResultBadge = (result: string | undefined): React.ReactNode => {
    if (!result) return '-';
    if (resultType === 'competency' || resultType === 'disease_care') {
      return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-700">{result}</span>;
    }
    return (
      <ScenarioResultBadge
        ok={isScenarioAnswerCorrect(result)}
        labelCorrect={tScenario('gcsEvaluationResult.resultCorrect')}
        labelWrong={tScenario('gcsEvaluationResult.resultWrong')}
      />
    );
  };

  const getResultCell = (row: any): React.ReactNode => {
    if (resultType === 'resp_infect' && row.correctRate != null) {
      return <span className="text-gray-900">{`${row.correctRate}%`}</span>;
    }
    return getResultBadge(row.result);
  };

  return (
    <div className="space-y-6 !mt-6 !mb-4">
      <h3 className="text-lg md:text-xl font-bold text-gray-900">{title}</h3>

      {(hasPatientInfo || (prescribedOrder != null && prescribedOrder !== '')) && (
        <div className="mb-4 space-y-1">
          {hasPatientInfo && (
            <div className="font-semibold text-gray-900">
              {[patientInfo.name, patientInfo.age != null ? String(patientInfo.age) : null, patientInfo.gender]
                .filter(Boolean)
                .join(' ')}
              {patientInfo.registrationNo != null && patientInfo.registrationNo !== ''
                ? ` (${tScenario('ivResult.registrationNo')}: ${patientInfo.registrationNo})`
                : ''}
            </div>
          )}
          {prescribedOrder != null && prescribedOrder !== '' && (
            <div className="text-gray-700 whitespace-pre-line">
              {tScenario('ivResult.prescribedOrder')} : {prescribedOrder}
            </div>
          )}
        </div>
      )}

      <div className="rounded-xl border border-gray-200 overflow-hidden bg-white overflow-x-auto">
        <table className="w-full text-sm text-center">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-4 py-3 font-semibold text-gray-700 text-center w-12">{tScenario('ivResult.tableNo')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-start">{tScenario('ivResult.tableEvaluationItem')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-start">{tScenario('ivResult.tableCorrect')}</th>
              <th className="px-4 py-3 font-semibold text-gray-700 text-center">{resultHeaderLabel}</th>
              {showScoreColumn && <th className="px-4 py-3 font-semibold text-gray-700 text-center">{tScenario('ivResult.tableScore')}</th>}
              <th className="px-4 py-3 font-semibold text-gray-700 text-center w-24 whitespace-nowrap">{tScenario('ivResult.tableComment')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const isExpanded = currentExpandedIndex === idx;
              const toggleComment = () => updateExpandedIndex((prev) => (prev === idx ? null : idx));
              return (
                <ExpandableCommentTableRows
                  key={idx}
                  isExpanded={isExpanded}
                  toggleComment={toggleComment}
                  colSpan={showScoreColumn ? 5 : 4}
                  comment={row.comment}
                >
                  <td className="px-4 py-3 text-center text-gray-900">{row.no ?? idx + 1}</td>
                  <td className="px-4 py-3 text-start text-gray-900">{row.evaluationItem ?? '-'}</td>
                  <td className="px-4 py-3 text-start text-gray-700 text-sm">
                    <ProcedureReferenceCellContent row={row} />
                  </td>
                  <td className="px-4 py-3 text-center">{getResultCell(row)}</td>
                  {showScoreColumn && (
                    <td className="px-4 py-3 text-center text-gray-900">{row.score != null ? String(row.score) : '-'}</td>
                  )}
                  <td className="px-4 py-3 text-center text-gray-600">
                    <CommentToggleButton
                      isExpanded={isExpanded}
                      onToggle={toggleComment}
                      ariaLabel={isExpanded ? t('simulationResults.collapseComment') : t('simulationResults.expandComment')}
                    />
                  </td>
                </ExpandableCommentTableRows>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface ProcedureResultProps {
  resultMetadata: unknown;
  /** 시뮬레이션 결과 본문 분기용 (예: ls000001~020 빈 데이터 안내) */
  contentCode?: string | null;
  /** 전체 참가자 뷰 여부 */
  isAllParticipantsView?: boolean;
}

/** IV·채혈·ACLS 등 절차형 수행결과 — `resultMetadata`에 따라 여러 `ProcedureView` 블록을 쌓음 */
export default function ProcedureResult({ resultMetadata, contentCode, isAllParticipantsView = false }: ProcedureResultProps): React.ReactElement {
  const { t } = useTranslation('class');
  const title = t('simulationResults.tablePerformance');
  const m = resultMetadata as Record<string, unknown> | null | undefined;

  const [expandedIvCommentRow, setExpandedIvCommentRow] = useState<number | null>(null);
  const [expandedBloodDrawCommentRow, setExpandedBloodDrawCommentRow] = useState<number | null>(null);
  const [expandedAclsCommentRow, setExpandedAclsCommentRow] = useState<number | null>(null);
  const [expandedRespInfectCommentRow, setExpandedRespInfectCommentRow] = useState<number | null>(null);
  const [expandedDiseaseCareCommentRow, setExpandedDiseaseCareCommentRow] = useState<number | null>(null);
  const [expandedCompetencyCommentRow, setExpandedCompetencyCommentRow] = useState<number | null>(null);
  const [expandedPpeCommentRow, setExpandedPpeCommentRow] = useState<number | null>(null);

  if (m == null || typeof m !== 'object') {
    return (
      <ResultSectionWithTitle title={title} lead="activity">
        <ProcedurePerformanceTableSkeleton showScoreColumn />
      </ResultSectionWithTitle>
    );
  }

  // COVID-19 또는 ACLS 시나리오는 별도 컴포넌트로 처리
  const scenarioType = m.scenarioType as string | undefined;
  if (scenarioType === 'covid19') {
    const Covid19Result = React.lazy(() => import('./Covid19Result'));
    return (
      <React.Suspense fallback={<div className="text-gray-500 text-sm">로딩 중...</div>}>
        <Covid19Result resultMetadata={resultMetadata} />
      </React.Suspense>
    );
  }
  
  if (scenarioType === 'acls') {
    const AclsResult = React.lazy(() => import('./AclsResult'));
    return (
      <React.Suspense fallback={<div className="text-gray-500 text-sm">로딩 중...</div>}>
        <AclsResult resultMetadata={resultMetadata} isAllParticipantsView={isAllParticipantsView} />
      </React.Suspense>
    );
  }

  const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

  const iv = m.ivPerformance;
  const bloodDraw = m.bloodDrawPerformance;
  const aclsRates = m.aclsItemRates;
  const aclsPerf = m.aclsPerformance;
  const respRates = m.respInfectItemRates;
  const respPerf = m.respInfectPerformance;
  const compRates = m.competencyItemRates;
  const compPerf = m.competencyPerformance;
  const disRates = m.diseaseCareItemRates;
  const disPerf = m.diseaseCarePerformance;
  const ppe = m.ppePerformance;

  // 정맥주사 데이터 확인용 로그
  if (iv || m.ivPatientInfo || m.ivPrescribedOrder) {
    console.log('[IV 데이터 확인]', {
      ivPerformance: iv,
      ivPatientInfo: m.ivPatientInfo,
      ivPrescribedOrder: m.ivPrescribedOrder,
      resultMetadata: m,
    });
  }

  const len = (v: unknown): number => (Array.isArray(v) ? v.length : 0);

  const hasAnyProcedureBlock =
    (Array.isArray(ppe) && ppe.length > 0)
    || (Array.isArray(iv) && iv.length > 0)
    || (Array.isArray(bloodDraw) && bloodDraw.length > 0)
    || len(aclsRates) + len(aclsPerf) > 0
    || len(respRates) + len(respPerf) > 0
    || len(compRates) + len(compPerf) > 0
    || len(disRates) + len(disPerf) > 0;

  const showNoDataEmpty =
    !hasAnyProcedureBlock && (isLsCoreNursingProcedureContentCode(contentCode ?? null) || isAoDiseaseCareContentCode(contentCode ?? null));

  return (
    <>
      {showNoDataEmpty && (
        <div className="space-y-3 !mt-6 !mb-4">
          <p className="text-sm text-gray-500">{t('simulationResults.lsProcedureNoRows')}</p>
          <ProcedurePerformanceTableSkeleton showScoreColumn={false} />
        </div>
      )}
      {Array.isArray(ppe) && ppe.length > 0 && (
        <ProcedureView
          title={title}
          rows={ppe}
          patientInfo={m.ppePatientInfo}
          prescribedOrder={str(m.ppePrescribedOrder)}
          resultType="ppe"
          expandedIndex={expandedPpeCommentRow}
          setExpandedIndex={setExpandedPpeCommentRow}
          contentCode={contentCode}
        />
      )}

      {Array.isArray(iv) && iv.length > 0 && (
        <ProcedureView
          title={title}
          rows={iv}
          patientInfo={m.ivPatientInfo}
          prescribedOrder={str(m.ivPrescribedOrder)}
          resultType="iv"
          expandedIndex={expandedIvCommentRow}
          setExpandedIndex={setExpandedIvCommentRow}
          contentCode={contentCode}
        />
      )}

      {Array.isArray(bloodDraw) && bloodDraw.length > 0 && (
        <ProcedureView
          title={title}
          rows={bloodDraw}
          patientInfo={m.bloodDrawPatientInfo}
          prescribedOrder={str(m.bloodDrawPrescribedOrder)}
          resultType="blood_draw"
          expandedIndex={expandedBloodDrawCommentRow}
          setExpandedIndex={setExpandedBloodDrawCommentRow}
          contentCode={contentCode}
        />
      )}

      {len(aclsRates) + len(aclsPerf) > 0 && (
        <ProcedureView
          title={title}
          rows={Array.isArray(aclsPerf) ? aclsPerf : []}
          resultType="acls"
          expandedIndex={expandedAclsCommentRow}
          setExpandedIndex={setExpandedAclsCommentRow}
          contentCode={contentCode}
        />
      )}

      {len(respRates) + len(respPerf) > 0 && (
        <ProcedureView
          title={title}
          rows={Array.isArray(respPerf) ? respPerf : []}
          resultType="resp_infect"
          expandedIndex={expandedRespInfectCommentRow}
          setExpandedIndex={setExpandedRespInfectCommentRow}
          contentCode={contentCode}
        />
      )}

      {len(compRates) + len(compPerf) > 0 && (
        <ProcedureView
          title={title}
          rows={Array.isArray(compPerf) ? compPerf : []}
          resultType="competency"
          expandedIndex={expandedCompetencyCommentRow}
          setExpandedIndex={setExpandedCompetencyCommentRow}
          contentCode={contentCode}
        />
      )}

      {len(disRates) + len(disPerf) > 0 && (
        <ProcedureView
          title={title}
          rows={Array.isArray(disPerf) ? disPerf : []}
          resultType="disease_care"
          expandedIndex={expandedDiseaseCareCommentRow}
          setExpandedIndex={setExpandedDiseaseCareCommentRow}
          contentCode={contentCode}
        />
      )}

      {!hasAnyProcedureBlock && !showNoDataEmpty && (
        <ResultSectionWithTitle title={title} lead="activity">
          <ProcedurePerformanceTableSkeleton showScoreColumn />
        </ResultSectionWithTitle>
      )}
    </>
  );
}
